import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  ThemeProvider,
  createTheme,
  useTheme,
  Fade,
  Slide
} from '@mui/material';
import MobileBookingSummary from './MobileBookingSummary';
import MobileServiceSelection from './MobileServiceSelection';
import MobilePractitionerSelection from './MobilePractitionerSelection';
import MobileDateTimePicker from './MobileDateTimePicker';
import MobilePatientForm from './MobilePatientForm';
import MobileConfirmation from './MobileConfirmation';
import PaymentForm from '../PaymentForm';
import {
  getClinicInfo,
  getClinicServices,
  getAvailableTimes,
  createBooking,
  getPractitioners,
  sendBloodTestEmail
} from '../../services/bookingService';
import { useBloodTestParams } from '../hooks/useBloodTestParams';
import inactivityTimeout from '../../utils/inactivityTimeout';

// Step definitions
const STEPS = {
  SERVICE: 1,
  PRACTITIONER: 2,
  DATETIME: 3,
  DETAILS: 4,
  PAYMENT: 5,
  CONFIRMATION: 6
};

const MobileBookingPage = () => {
  const { clinicStub } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const defaultTheme = useTheme();

  // URL parameters
  const serviceId = searchParams.get('service');
  const serviceIds = searchParams.get('services'); // Support multiple services: ?services=49,13
  const category = searchParams.get('category');
  const returnUrl = searchParams.get('returnUrl');
  const displayMode = searchParams.get('display');
  const showInfo = searchParams.get('showInfo') === 'true';  // Auto-show info dialog for pre-selected service

  // Blood test params
  const bloodTestParams = useBloodTestParams();
  const [bloodTestEmailStatus, setBloodTestEmailStatus] = useState('pending');

  // Refs for scrolling
  const contentRef = useRef(null);
  const dateTimeRef = useRef(null);

  // Theme
  const [customTheme, setCustomTheme] = useState(defaultTheme);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [clinic, setClinic] = useState(null);
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);

  // Booking state
  const [step, setStep] = useState(STEPS.SERVICE);
  const [booking, setBooking] = useState({
    service: null,
    practitioner: null,
    timezone: detectBrowserTimezone(),
    selectedDate: null,
    selectedTime: null,
    patient: {
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    },
    notes: '',
    payment: { status: 'pending' }
  });

  // Detect browser timezone
  function detectBrowserTimezone() {
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const supportedTimezones = [
        'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
        'Australia/Adelaide', 'Australia/Perth', 'Australia/Hobart',
        'Australia/Darwin', 'Australia/Canberra', 'Pacific/Auckland',
        'Pacific/Chatham'
      ];
      return supportedTimezones.includes(browserTimezone) ? browserTimezone : 'Australia/Adelaide';
    } catch {
      return 'Australia/Adelaide';
    }
  }

  // Initialize inactivity timeout
  useEffect(() => {
    inactivityTimeout.init();
    return () => inactivityTimeout.stop();
  }, []);

  // Load clinic data
  useEffect(() => {
    if (clinicStub === 'admin') {
      setLoading(false);
      return;
    }

    const fetchClinic = async () => {
      try {
        setLoading(true);
        setError(null);

        const clinicResult = await getClinicInfo(null, clinicStub);
        if (!clinicResult?.clinic) {
          throw new Error('Clinic not found');
        }

        setClinic(clinicResult.clinic);

        // Custom theme
        if (clinicResult.clinic.primary_color) {
          setCustomTheme(createTheme({
            ...defaultTheme,
            palette: {
              ...defaultTheme.palette,
              primary: { main: clinicResult.clinic.primary_color }
            }
          }));
        }

        // Fetch services
        const servicesResult = await getClinicServices(clinicResult.clinic.id, category);
        setServices(servicesResult || []);

        // Fetch practitioners
        const practitionersResult = await getPractitioners(clinicResult.clinic.id);
        setPractitioners(practitionersResult.practitioners || []);

        // Pre-select service if provided in URL
        if (serviceId && servicesResult) {
          const selectedService = servicesResult.find(
            s => s.id.toString() === serviceId.toString()
          );
          if (selectedService) {
            // If showInfo=true, stay on service step and let the info dialog show first
            // The MobileServiceSelection component will handle showing the dialog and proceeding
            if (showInfo) {
              // Stay on SERVICE step - the component will show the info dialog
              console.log('showInfo mode: staying on service step to show info dialog');
            } else {
              // Normal flow: select and advance to practitioner
              handleServiceSelect(selectedService);
            }
          }
        }

      } catch (err) {
        console.error('Error loading clinic:', err);
        setError(err.message || 'Failed to load clinic data');
      } finally {
        setLoading(false);
      }
    };

    fetchClinic();
  }, [clinicStub, serviceId, category]);

  // Fetch available times when service and practitioner are selected
  const fetchAvailableTimes = useCallback(async () => {
    if (!booking.service || !booking.practitioner || !clinic) return;

    try {
      setLoadingTimes(true);
      setAvailableTimes([]);

      const today = new Date();
      const allTimes = [];

      // Fetch 7 days of availability
      for (let i = 0; i < 7; i++) {
        const day = new Date(today);
        day.setDate(today.getDate() + i);

        const dateStr = day.toISOString().split('T')[0];

        try {
          if (booking.practitioner.id === 'any') {
            // Fetch for all practitioners
            const practitionerIds = clinic.practitioner_ids || [];
            for (const practitionerId of practitionerIds) {
              const result = await getAvailableTimes({
                clinic_id: clinic.id.toString(),
                appointment_type: booking.service.id,
                practitioner_id: practitionerId,
                from: dateStr,
                to: dateStr
              }, booking.timezone);

              if (result?.available_times?.length > 0) {
                result.available_times.forEach(time => {
                  time.original_practitioner_id = practitionerId;
                  allTimes.push(time);
                });
              }
            }
          } else {
            const result = await getAvailableTimes({
              clinic_id: clinic.id.toString(),
              appointment_type: booking.service.id,
              practitioner_id: booking.practitioner.id,
              from: dateStr,
              to: dateStr
            }, booking.timezone);

            if (result?.available_times?.length > 0) {
              result.available_times.forEach(time => {
                time.original_practitioner_id = booking.practitioner.id;
                allTimes.push(time);
              });
            }
          }
        } catch (err) {
          console.warn(`Error fetching times for ${dateStr}:`, err);
        }

        // Small delay to avoid rate limiting
        if (i < 6) await new Promise(r => setTimeout(r, 200));
      }

      // Sort and deduplicate
      const sortedTimes = allTimes.sort((a, b) =>
        new Date(a.iso) - new Date(b.iso)
      );

      // Deduplicate if "any practitioner"
      if (booking.practitioner.id === 'any') {
        const seen = new Set();
        const unique = sortedTimes.filter(time => {
          if (seen.has(time.iso)) return false;
          seen.add(time.iso);
          return true;
        });
        setAvailableTimes(unique);
      } else {
        setAvailableTimes(sortedTimes);
      }

    } catch (err) {
      console.error('Error fetching times:', err);
      setError('Unable to load available times. Please try again.');
    } finally {
      setLoadingTimes(false);
    }
  }, [booking.service, booking.practitioner, clinic, booking.timezone]);

  // Trigger time fetch when moving to datetime step
  useEffect(() => {
    if (step === STEPS.DATETIME && booking.service && booking.practitioner) {
      fetchAvailableTimes();
    }
  }, [step, fetchAvailableTimes]);

  // Scroll to content when step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [step]);

  // Handlers
  const handleServiceSelect = async (service) => {
    setBooking(prev => ({
      ...prev,
      service,
      practitioner: null,
      selectedDate: null,
      selectedTime: null
    }));

    // Fetch practitioners for this service
    if (clinic && service) {
      try {
        const result = await getPractitioners(clinic.id, service.id);
        setPractitioners(result.practitioners || []);

        // If only one practitioner, auto-select and skip to datetime
        if (result.practitioners?.length === 1) {
          setBooking(prev => ({
            ...prev,
            practitioner: result.practitioners[0]
          }));
          setStep(STEPS.DATETIME);
        } else {
          setStep(STEPS.PRACTITIONER);
        }
      } catch (err) {
        console.error('Error loading practitioners:', err);
        setStep(STEPS.PRACTITIONER);
      }
    } else {
      setStep(STEPS.PRACTITIONER);
    }
  };

  const handlePractitionerSelect = (practitioner) => {
    setBooking(prev => ({
      ...prev,
      practitioner,
      selectedDate: null,
      selectedTime: null
    }));
    setStep(STEPS.DATETIME);
  };

  const handleTimeSelect = (time) => {
    // If "any practitioner", get the actual practitioner from the time slot
    let actualPractitioner = booking.practitioner;
    if (booking.practitioner.id === 'any' && time.practitioner_id) {
      const found = practitioners.find(p => p.id === time.practitioner_id);
      if (found) actualPractitioner = found;
    }

    setBooking(prev => ({
      ...prev,
      practitioner: actualPractitioner,
      selectedTime: time
    }));
    setStep(STEPS.DETAILS);
  };

  const handlePatientSubmit = async (patientData, notes) => {
    // Send blood test email if required
    if (bloodTestParams.bloodTestRequired && patientData.email) {
      setBloodTestEmailStatus('sending');
      try {
        await sendBloodTestEmail({
          email: patientData.email,
          bloodTestUrl: bloodTestParams.bloodTestUrl,
          bloodTestType: bloodTestParams.bloodTestType,
          bloodTestPrice: bloodTestParams.bloodTestPrice
        });
        setBloodTestEmailStatus('sent');
      } catch {
        setBloodTestEmailStatus('failed');
      }
    }

    const updatedBooking = {
      ...booking,
      patient: patientData,
      notes
    };

    // Check if free appointment
    const price = booking.service?.price;
    const isFree = price === null || price === 0 || price === '0' || parseFloat(price) === 0;

    if (isFree) {
      updatedBooking.payment = { status: 'complete', transactionId: 'free-appointment' };
      setBooking(updatedBooking);

      // Save to session storage
      try {
        sessionStorage.setItem('bookingSession', JSON.stringify(updatedBooking));
        localStorage.setItem('currentBooking', JSON.stringify({
          clinicId: clinic.id,
          clinicStub,
          serviceId: booking.service.id,
          practitionerId: booking.practitioner.id
        }));
      } catch (e) {
        console.error('Error saving booking:', e);
      }

      // Skip payment, go to confirmation
      await handleConfirmBooking(updatedBooking);
    } else {
      setBooking(updatedBooking);
      setStep(STEPS.PAYMENT);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    const updatedBooking = {
      ...booking,
      payment: {
        status: 'complete',
        transactionId: paymentData.transactionId,
        accessCode: paymentData.accessCode,
        appointment_created: paymentData.appointment_created,
        appointment_id: paymentData.appointment_id
      }
    };
    setBooking(updatedBooking);

    if (paymentData.appointment_created) {
      setStep(STEPS.CONFIRMATION);
    } else {
      await handleConfirmBooking(updatedBooking);
    }
  };

  const handleConfirmBooking = async (bookingToConfirm = booking) => {
    try {
      setLoading(true);

      const startTime = new Date(bookingToConfirm.selectedTime.iso);
      const endTime = new Date(startTime.getTime() + (bookingToConfirm.service.duration_in_minutes * 60000));

      const transactionId = bookingToConfirm.payment?.transactionId || 'payment-completed';
      const paymentNotes = `Payment completed: Transaction ID ${transactionId}`;
      const combinedNotes = bookingToConfirm.notes
        ? `${bookingToConfirm.notes}\n\n${paymentNotes}`
        : paymentNotes;

      await createBooking({
        clinic_id: clinic.id.toString(),
        appointment_type_id: bookingToConfirm.service.id,
        practitioner_id: bookingToConfirm.practitioner.id,
        starts_at: startTime.toISOString(),
        ends_at: endTime.toISOString(),
        patient_first_name: bookingToConfirm.patient.first_name,
        patient_last_name: bookingToConfirm.patient.last_name,
        patient_email: bookingToConfirm.patient.email,
        patient_phone: bookingToConfirm.patient.phone,
        notes: combinedNotes
      });

      // Clean up storage
      sessionStorage.removeItem('bookingSession');
      localStorage.removeItem('currentBooking');

      setStep(STEPS.CONFIRMATION);

    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === STEPS.PRACTITIONER) setStep(STEPS.SERVICE);
    else if (step === STEPS.DATETIME) setStep(STEPS.PRACTITIONER);
    else if (step === STEPS.DETAILS) setStep(STEPS.DATETIME);
    else if (step === STEPS.PAYMENT) setStep(STEPS.DETAILS);
  };

  const handleSummaryClick = (targetStep) => {
    if (targetStep < step) {
      setStep(targetStep);
    }
  };

  const handleReturnHome = () => {
    if (returnUrl) {
      try {
        window.location.href = decodeURIComponent(returnUrl);
        return;
      } catch (e) {
        console.error('Error decoding returnUrl:', e);
      }
    }
    window.location.href = `/${clinicStub}`;
  };

  // Render step content
  const renderContent = () => {
    switch (step) {
      case STEPS.SERVICE:
        return (
          <MobileServiceSelection
            services={services}
            selectedService={booking.service}
            onSelect={handleServiceSelect}
            loading={loading}
            displayMode={displayMode || clinic?.display_mode || 'flat'}
            showInfoForServiceId={showInfo ? serviceId : null}
            filterServiceIds={serviceIds ? serviceIds.split(',') : null}
          />
        );

      case STEPS.PRACTITIONER:
        return (
          <MobilePractitionerSelection
            practitioners={practitioners}
            selectedPractitioner={booking.practitioner}
            onSelect={handlePractitionerSelect}
            onBack={handleBack}
            loading={loading}
          />
        );

      case STEPS.DATETIME:
        return (
          <MobileDateTimePicker
            ref={dateTimeRef}
            availableTimes={availableTimes}
            selectedTime={booking.selectedTime}
            onTimeSelect={handleTimeSelect}
            onBack={handleBack}
            loading={loadingTimes}
            timezone={booking.timezone}
          />
        );

      case STEPS.DETAILS:
        return (
          <MobilePatientForm
            patientInfo={booking.patient}
            notes={booking.notes}
            onSubmit={handlePatientSubmit}
            onBack={handleBack}
            loading={loading}
            bloodTestRequired={bloodTestParams.bloodTestRequired}
            bloodTestPrice={bloodTestParams.bloodTestPrice}
          />
        );

      case STEPS.PAYMENT:
        const startTime = new Date(booking.selectedTime.iso);
        const endTime = new Date(startTime.getTime() + (booking.service.duration_in_minutes * 60000));

        return (
          <PaymentForm
            clinicId={clinic.id.toString()}
            serviceId={booking.service.id}
            practitionerId={booking.practitioner.id}
            amount={Number(booking.service.price) || 0}
            patientEmail={booking.patient.email}
            patientName={`${booking.patient.first_name} ${booking.patient.last_name}`}
            appointmentDetails={{
              appointment_type_id: booking.service.id,
              starts_at: startTime.toISOString(),
              ends_at: endTime.toISOString(),
              patient_first_name: booking.patient.first_name,
              patient_last_name: booking.patient.last_name,
              patient_phone: booking.patient.phone,
              notes: booking.notes
            }}
            returnUrl={returnUrl}
            onSuccess={handlePaymentSuccess}
            onCancel={handleBack}
            loading={loading}
          />
        );

      case STEPS.CONFIRMATION:
        return (
          <MobileConfirmation
            booking={booking}
            clinic={clinic}
            onReturnHome={handleReturnHome}
          />
        );

      default:
        return null;
    }
  };

  // Main render
  if (loading && step === STEPS.SERVICE) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        pb: 4
      }}>
        {/* Clinic Header */}
        <Box sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          py: 2,
          px: 2,
          textAlign: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          {clinic?.logo_url && (
            <img
              src={clinic.logo_url}
              alt={clinic.name}
              style={{ maxHeight: 40, marginBottom: 8 }}
            />
          )}
          <Typography variant="subtitle1" fontWeight="600" color="text.primary">
            {clinic?.name || 'Book Appointment'}
          </Typography>
        </Box>

        {/* Booking Summary (sticky) */}
        {step > STEPS.SERVICE && step < STEPS.CONFIRMATION && (
          <MobileBookingSummary
            booking={booking}
            currentStep={step}
            onStepClick={handleSummaryClick}
          />
        )}

        {/* Error Alert */}
        {error && (
          <Container maxWidth="sm" sx={{ mt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Container>
        )}

        {/* Main Content */}
        <Container
          maxWidth="sm"
          ref={contentRef}
          sx={{ mt: 2, px: 2 }}
        >
          <Fade in={true} timeout={300}>
            <Box>
              {renderContent()}
            </Box>
          </Fade>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default MobileBookingPage;
